import { isSport } from "../../common/sportFunctions.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import { minorsProspects, organizationPlayers } from "../core/trade/organizationPlayers.ts";
import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import type { TradeTeams, UpdateEvents } from "../../common/types.ts";
import isUntradable from "../core/trade/isUntradable.ts";
import makeItWork, { improveBlockOffer } from "../core/trade/makeItWork.ts";
import summary from "../core/trade/summary.ts";
import {
	augmentOffers,
	getOffers as getBlockOffers,
	toConciseLookingFor,
} from "../api/index.ts";
import { shuffle, uniformSeed, choice } from "../../common/random.ts";
import { ValueChangeCalculator } from "../core/team/ValueChangeCalculator.ts";
import { orderBy } from "../../common/utils.ts";

// Each team takes the negotiables it needs: the ones it values above what the league does on average (that excess is
// its roster fit), up to four, or at least the one it needs most. Then it offers what it would give for them.
const MAX_CHOSEN = 4;
const NEED_MARGIN = 1.05;
const getChosenOffers = async (
	pids: number[],
	dpids: number[],
	lookingFor: Parameters<typeof getBlockOffers>[2],
) => {
	const userTid = g.get("userTid");
	const valueChangeCalculator = new ValueChangeCalculator();
	const noPreferences =
		lookingFor.positions.size === 0 &&
		lookingFor.skills.size === 0 &&
		!lookingFor.draftPicks &&
		!lookingFor.prospects &&
		!lookingFor.bestCurrentPlayers;
	// Same order as the trading block (by city), not the order teams are stored in
	const teams = orderBy(
		(await idb.cache.teams.getAll()).filter((t) => !t.disabled && t.tid !== userTid),
		["region", "name", "tid"],
	);
	const assets = [
		...pids.map((pid) => ({ pid, dpid: undefined as number | undefined })),
		...dpids.map((dpid) => ({ pid: undefined as number | undefined, dpid })),
	];
	// What each team would gain from each negotiable alone
	const dvs = new Map<number, number[]>();
	for (const t of teams) {
		const row = [];
		for (const asset of assets) {
			row.push(
				await valueChangeCalculator.evaluate({
					tid: t.tid,
					pidsAdd: asset.pid === undefined ? [] : [asset.pid],
					pidsRemove: [],
					dpidsAdd: asset.dpid === undefined ? [] : [asset.dpid],
					dpidsRemove: [],
					tradingPartnerTid: userTid,
				}),
			);
		}
		dvs.set(t.tid, row);
	}
	const average = assets.map(
		(_, i) => teams.reduce((sum, t) => sum + dvs.get(t.tid)![i]!, 0) / teams.length,
	);

	const offers: TradeTeams[] = [];
	for (const t of teams) {
		const row = dvs.get(t.tid)!;
		const need = assets
			.map((asset, i) => ({ ...asset, dv: row[i]!, fit: average[i]! > 0 ? row[i]! / average[i]! : 0 }))
			.filter((c) => c.dv > 0)
			.sort((a, b) => b.fit - a.fit);
		if (need.length === 0) {
			continue;
		}
		const wanted = need.filter((c) => c.fit >= NEED_MARGIN);
		const chosen = (wanted.length > 0 ? wanted : need).slice(0, wanted.length > 0 ? MAX_CHOSEN : 1);
		const teams0: TradeTeams = [
			{
				tid: userTid,
				pids: chosen.flatMap((c) => (c.pid === undefined ? [] : [c.pid])),
				pidsExcluded: [],
				dpids: chosen.flatMap((c) => (c.dpid === undefined ? [] : [c.dpid])),
				dpidsExcluded: [],
			},
			{ tid: t.tid, pids: [], pidsExcluded: [], dpids: [], dpidsExcluded: [] },
		];
		const maxAssetsToAdd = 4 + chosen.length;
		const result = await makeItWork(teams0, {
			holdUserConstant: true,
			maxAssetsToAdd,
			lookingFor,
			valueChangeCalculator,
		});
		if (result) {
			offers.push(
				noPreferences
					? await improveBlockOffer(result, { maxAssetsToAdd, valueChangeCalculator })
					: result,
			);
		}
	}
	return offers;
};

// Baseball: every team that wants the offered package (the whole set of negotiables, or the part the user picked)
// proposes what it would give for it, like the trading block
const getPackageOffers = async () => {
	const userTid = g.get("userTid");
	const saved = g.get("tradeProposalsLookingFor")?.[userTid];
	const lookingFor = saved
		? toConciseLookingFor(saved)
		: { positions: new Set<string>(), skills: new Set<string>(), draftPicks: false, prospects: false, bestCurrentPlayers: false };
	const availablePids = new Set(g.get("tradeProposalsAvailable")?.[userTid] ?? []);
	const availableDpids = new Set(g.get("tradeProposalsAvailableDpids")?.[userTid] ?? []);
	const tradable = new Set(
		(await organizationPlayers(userTid))
			.filter((p) => availablePids.has(p.pid) && !isUntradable(p).untradable)
			.map((p) => p.pid),
	);
	const ownDpids = new Set(
		(await idb.cache.draftPicks.indexGetAll("draftPicksByTid", userTid)).map((dp) => dp.dpid),
	);
	const offered = g.get("tradeProposalsOffered")?.[userTid];
	const whole = !offered || offered.aiChooses;
	const pids = (whole ? [...tradable] : offered.pids).filter((pid) => tradable.has(pid));
	const dpids = (whole ? [...availableDpids] : offered.dpids).filter(
		(dpid) => availableDpids.has(dpid) && ownDpids.has(dpid),
	);
	if (pids.length === 0 && dpids.length === 0) {
		return [];
	}

	const offers: TradeTeams[] = [];
	const found = offered?.aiChooses
		? await getChosenOffers(pids, dpids, lookingFor)
		: await getBlockOffers(pids, dpids, lookingFor);
	for (const teams of found) {
		// An offer for nothing, or one the rules block (salary, maximum contracts), isn't an offer
		if (teams[1].pids.length === 0 && teams[1].dpids.length === 0) {
			continue;
		}
		if ((await summary(teams)).warning) {
			continue;
		}
		offers.push(teams);
	}
	return augmentOffers(offers);
};

const getOffers = async (seed: number) => {
	const NUM_OFFERS = 5;
	const NUM_TRIES_PER_TEAM = 10;

	const userTid = g.get("userTid");

	const teams = (await idb.cache.teams.getAll()).filter(
		(t) => !t.disabled && t.tid !== userTid,
	);
	shuffle(teams, seed);

	const players = (
		await idb.cache.players.indexGetAll("playersByTid", userTid)
	).filter((p) => !isUntradable(p).untradable);
	const draftPicks = await idb.cache.draftPicks.indexGetAll(
		"draftPicksByTid",
		userTid,
	);

	if (players.length === 0 && draftPicks.length === 0) {
		return [];
	}

	const offers: TradeTeams[] = [];

	const valueChangeCalculator = new ValueChangeCalculator();

	for (const t of teams) {
		for (let i = 0; i < NUM_TRIES_PER_TEAM; i++) {
			const seedBase = seed + NUM_TRIES_PER_TEAM * t.tid + i;
			const r = uniformSeed(seedBase);
			const pids: number[] = [];
			const dpids: number[] = [];

			if ((r < 0.7 || draftPicks.length === 0) && players.length > 0) {
				// Weight by player value - good player more likely to be in trade
				pids.push(choice(players, (p) => p.value, seedBase + 1).pid);
			} else if ((r < 0.85 || players.length === 0) && draftPicks.length > 0) {
				dpids.push(choice(draftPicks, undefined, seedBase + 2).dpid);
			} else {
				pids.push(choice(players, (p) => p.value, seedBase + 3).pid);
				dpids.push(choice(draftPicks, undefined, seedBase + 4).dpid);
			}

			const teams0: TradeTeams = [
				{
					dpids,
					dpidsExcluded: [],
					pids,
					pidsExcluded: [],
					tid: userTid,
				},
				{
					dpids: [],
					dpidsExcluded: [],
					pids: [],
					pidsExcluded: [],
					tid: t.tid,
				},
			];

			const teams = await makeItWork(teams0, {
				holdUserConstant: false,
				maxAssetsToAdd: 5,
				valueChangeCalculator,
			});

			if (!teams) {
				continue;
			}

			// Don't do trades of just picks, it's weird usually
			if (teams[0].pids.length === 0 && teams[1].pids.length === 0) {
				continue;
			}

			// Don't do trades for nothing, it's weird usually
			if (teams[1].pids.length === 0 && teams[1].dpids.length === 0) {
				continue;
			}

			const tradeSummary = await summary(teams);

			// Try to find a no warning one
			if (tradeSummary.warning && i < NUM_TRIES_PER_TEAM - 1) {
				continue;
			}

			offers.push(teams);
			break;
		}

		if (offers.length >= NUM_OFFERS) {
			break;
		}
	}

	return augmentOffers(offers);
};

// offers[number].summary.trade includes players with no stats, and offers[number].players includes players with stats. Make them the same. Plus ratings and age!
export const fixPlayers = (
	offer: Awaited<ReturnType<typeof getOffers>>[number],
	summaryTeamsIndex: number,
	playersWithStats: any[],
) => {
	const t = offer.summary.teams[summaryTeamsIndex];
	if (!t) {
		throw new Error("Should never happen");
	}

	for (const p of t.trade) {
		const p2 = playersWithStats.find((p2) => p2.pid === p.pid);
		p.stats = p2.stats;
		p.ratings = p2.ratings;
		p.age = p2.age;
	}
};

const updateTradeProposals = async (
	inputs: unknown,
	updateEvents: UpdateEvents,
) => {
	if (
		updateEvents.includes("firstRun") ||
		updateEvents.includes("playerMovement") ||
		updateEvents.includes("gameSim") ||
		updateEvents.includes("newPhase") ||
		updateEvents.includes("g.tradeProposalsSeed") ||
		updateEvents.includes("gameAttributes")
	) {
		const teamSeason = await idb.cache.teamSeasons.indexGet(
			"teamSeasonsByTidSeason",
			[g.get("userTid"), g.get("season")],
		);
		const gp = teamSeason ? helpers.getTeamSeasonGp(teamSeason) : 0;

		const NUM_GAMES_BEFORE_NEW_OFFERS = 10;

		const seed =
			Math.floor(gp / NUM_GAMES_BEFORE_NEW_OFFERS) +
			g.get("season") +
			g.get("phase") +
			g.get("tradeProposalsSeed");

		const userTid = g.get("userTid");
		const proposalStats = ["gp", "keyStats", "war"];
		const organization = isSport("baseball") ? await organizationPlayers(userTid) : [];
		const prospectPids = minorsProspects(organization);
		const proposalPlayers = isSport("baseball") ? addFirstNameShort(await idb.getCopies.playersPlus(
			organization.filter((p) => !isUntradable(p).untradable),
			{
				attrs: ["pid", "tid", "firstName", "lastName", "age", "contract", "injury", "watch", "jerseyNumber", "draft"],
				ratings: ["pos", "ovr", "pot", "skills"],
				stats: proposalStats,
				season: g.get("season"),
				showNoStats: true,
				showRookies: true,
				fuzz: true,
			},
		)).map((p) => ({ ...p, prospect: prospectPids.has(p.pid) })) : [];
		const eligible = new Set(proposalPlayers.map((p) => p.pid));
		const selectedPids = (g.get("tradeProposalsAvailable")?.[userTid] ?? []).filter((pid) => eligible.has(pid));
		const proposalPicks = isSport("baseball") ? await Promise.all(
			(await idb.getCopies.draftPicks({ tid: userTid }, "noCopyCache")).map(async (dp) => ({
				...dp,
				desc: await helpers.pickDesc(dp),
			})),
		) : [];
		const eligibleDpids = new Set(proposalPicks.map((dp) => dp.dpid));
		const selectedDpids = (g.get("tradeProposalsAvailableDpids")?.[userTid] ?? []).filter((dpid) => eligibleDpids.has(dpid));
		// Opening the page must not wait for the expensive trade search.
		// Refresh is explicit so saved negotiables do not block navigation.
		const offers = !isSport("baseball")
			? await getOffers(seed)
			: updateEvents.includes("g.tradeProposalsSeed")
				? await getPackageOffers()
				: [];
		const savedOffer = g.get("tradeProposalsOffered")?.[userTid];

		for (const offer of offers) {
			fixPlayers(offer, 1, offer.players);
			fixPlayers(offer, 0, offer.playersUser);
		}

		return {
			offers,
			proposalPlayers,
			proposalStats,
			selectedPids,
			proposalPicks,
			selectedDpids,
			userTid,
			tradeProposalsAvailable: g.get("tradeProposalsAvailable") ?? {},
			tradeProposalsAvailableDpids: g.get("tradeProposalsAvailableDpids") ?? {},
			tradeProposalsLookingFor: g.get("tradeProposalsLookingFor") ?? {},
			tradeProposalsOffered: g.get("tradeProposalsOffered") ?? {},
			// "whole" = every negotiable together, "pick" = the part chosen, "ai" = each team takes what it needs
			offerMode: (!savedOffer ? "whole" : savedOffer.aiChooses ? "ai" : "pick") as "whole" | "pick" | "ai",
			offeredPids: savedOffer && !savedOffer.aiChooses ? savedOffer.pids.filter((pid) => eligible.has(pid)) : [],
			offeredDpids: savedOffer && !savedOffer.aiChooses ? savedOffer.dpids.filter((dpid) => eligibleDpids.has(dpid)) : [],
		};
	}
};

export default updateTradeProposals;
